import React, { useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import {
    Modal,
    Box,
    TextField,
    Typography,
    Stack,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import { networkUrls } from "../../services/networkurls";
import { DeleteChat, PostChat, PutChat } from "../../services/apiServices";
import "./UserModal.css";
const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: 3,
    boxShadow: 24,
    p: 4,
};

interface UserModalProps {
    open: boolean;
    setOpen: (open: { isOpen: boolean; type: string }) => void;
    type: string;
    setIsShowSnackbar?: (val: boolean) => void;
    setSnackbarInfo?: (info: { message: string; color: string }) => void;
    setTrigegerGetCall?: Dispatch<SetStateAction<number>>;
    data?: any;
}

const UserModal: React.FC<UserModalProps> = ({
    open,
    setOpen,
    type,
    setIsShowSnackbar,
    setSnackbarInfo,
    setTrigegerGetCall,
    data,
}) => {
    const [loading, setLoading] = useState(false);
    const ButtonText = type === "add" ? "Add" : type === "edit" ? "Update" : "Delete";

    const handleClose = () => setOpen({ isOpen: false, type: "" });

    const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required")
    .test(
      "absyz-domain",
      "Only absyz email addresses are allowed",
      (value) => {
        if (!value) return false;
        return value.toLowerCase().endsWith("@absyz.com");
      }
    ),
  role: Yup.string().required("Role is required"),
});


    const formik: any = useFormik({
        initialValues: {
            email: type === "edit" ? data?.email || "" : "",
            role: type === "edit" ? data?.role : ""
        },
        validationSchema: type === "delete" ? null : validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            if (type === "add") {
                addUser(values);
            } else if (type === "edit") {
                editUser(values);
            } else {
                handleDelete();
            }
        },
    });

    const addUser = async (values: any) => {
        setLoading(true);
        const payload = {
            email: values.email,
            role: values.role 
        };

        try {
            const response: any = await PostChat(`/api${networkUrls.addUser}`, payload);
            if (response?.status === 200) {
                if (setTrigegerGetCall) {
                    setTrigegerGetCall((prev: number) => prev + 1);
                }
                setIsShowSnackbar?.(true);
                setSnackbarInfo?.({
                    color: "success",
                    message: response?.data?.message || "User added successfully",
                });
                handleClose();
            } else {
                setIsShowSnackbar?.(true);
                setSnackbarInfo?.({
                    color: "error",
                    message: response?.data?.error || "Adding user failed",
                });
            }
        } catch (err) {
            console.log("Error adding user:", err);
        } finally {
            setLoading(false);
        }
    };

    const editUser = async (values: any) => {
        setLoading(true);
        const payload = {
            email: values.email,
            role: values.role
        };

        try {
            const response: any = await PutChat(`/api${networkUrls.updateUser}/${data?.user_id}`, payload);
            if (response?.status === 200) {
                if (setTrigegerGetCall) {
                    setTrigegerGetCall((prev: number) => prev + 1);
                }
                setIsShowSnackbar?.(true);
                setSnackbarInfo?.({
                    color: "success",
                    message: response?.data?.message || "User updated successfully",
                });
                handleClose();
            } else {
                setIsShowSnackbar?.(true);
                setSnackbarInfo?.({
                    color: "error",
                    message: response?.data?.message || "Updating user failed",
                });
            }
        } catch (err) {
            console.log("Error updating user:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        console.log("Deleting user with ID:", data);
        setLoading(true);
        try {
            const response: any = await DeleteChat(
                `/api${networkUrls.deleteUser}/${data?.user_id || data?.id}`
            );
            if (response?.status === 200) {
                if (setTrigegerGetCall) {
                    setTrigegerGetCall((prev: number) => prev + 1);
                }
                setIsShowSnackbar?.(true);
                setSnackbarInfo?.({
                    color: "success",
                    message: response?.data?.message || "User deleted successfully",
                });
                handleClose();
            } else {
                setIsShowSnackbar?.(true);
                setSnackbarInfo?.({
                    color: "error",
                    message: response?.data?.error || "Deleting user failed",
                });
            }
        } catch (err) {
            console.log("Error deleting user:", err);
        } finally {
            handleClose();
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={style} component="form" onSubmit={formik.handleSubmit} className="user-modal-container">
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "grey.600",
                    }}
                >
                    <CloseIcon />
                </IconButton>
                {type === "delete" ? (
                    <>
                        <Typography variant="h6" mb={2}>
                            Are you sure you want to delete this user?
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="h6" mb={2}>
                            {type === "add" ? "Add" : "Edit"} User
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                id="email"
                                name="email"
                                label="Email"
                                type="email"
                                value={formik.values.email}
                                autoComplete="off"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                            />

                          <TextField
                                fullWidth
                                id="role"
                                name="role"
                                label="Role"
                                type="text"
                                value={formik.values.role}
                                autoComplete="off"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.role && Boolean(formik.errors.role)}
                                helperText={formik.touched.role && formik.errors.role}
                            />
                        </Stack>
                    </>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
                    <Button
                        variant="outlined"
                        color="neutral"
                        onClick={handleClose}
                        type="button"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="solid"
                        sx={{ backgroundColor: "#4D8FAC" }}
                        loading={loading}
                    >
                        {ButtonText}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

export default UserModal;

